import React, { useState, useEffect } from "react";
import { Plus, Search, Package, Loader2, Truck, ChevronDown, ChevronUp, Sliders, MapPin, Hash, CheckSquare, FileText, DollarSign } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useInventory } from "../hooks/useInventory";
import { usePOs } from "../hooks/usePOs";
import { useCompanyLogos } from "../hooks/useCompanyLogos";
import { useLogistics } from "../hooks/useLogistics";
import { useCustomers } from "../hooks/useCustomers";
import { useZones } from "../hooks/useZones";
import { useBuildings } from "../hooks/useBuildings";

import ChangeRequestModal from "../components/modals/ChangeRequestModal";
import InventoryCreateModal from "../components/modals/InventoryCreateModal";
import InventoryNotesModal from "../components/modals/InventoryNotesModal";
import InventoryZoneModal from "../components/modals/InventoryZoneModal";
import InventoryStockModal from "../components/modals/InventoryStockModal";
import InventoryShipModal from "../components/modals/InventoryShipModal";
import EditShippingCostModal from "../components/modals/EditShippingCostModal";
import POTasksModal from "../components/modals/POTasksModal";
import { printPackingSlip, getAllPackingSlipsForPO } from "../utils/packingSlipExport";
import { isPoDelivered } from "../utils/deliveryStatus";
import { logSecurityEvent } from "../services/AuditLogger";
import { db } from "../firebase/config";
import { doc } from "firebase/firestore";

export default function Inventory() {
  const { currentUser } = useAuth();
  const { inventory, loading: invLoading, updateInventoryItem, addInventoryItem, appendInventoryLog } = useInventory();
  const { pos, updatePO } = usePOs();
  const { customers } = useCustomers();
  const { companyLogos } = useCompanyLogos();
  const { shipments, carriers: carrierObjects, updateShipment } = useLogistics();
  const carriers = (carrierObjects || []).map(c => c.name || c);
  const [selectedChangeRequestPo, setSelectedChangeRequestPo] = useState(null);
  const [selectedTasksPo, setSelectedTasksPo] = useState(null);

  const isPoFullyDelivered = (poToCheck) => {
    return isPoDelivered({ po: poToCheck, shipments });
  };

  const findPo = (idOrNumber, fallbackPoNum = null) => {
    if (!idOrNumber && !fallbackPoNum) return null;
    return pos.find(p => 
      (idOrNumber && p.id === idOrNumber) ||
      (idOrNumber && p.poNumber === idOrNumber) ||
      (fallbackPoNum && p.poNumber === fallbackPoNum) ||
      (fallbackPoNum && p.id === fallbackPoNum)
    ) || null;
  };

  const findShipment = (idOrNumber, fallbackPoNum = null) => {
    if (!idOrNumber && !fallbackPoNum) return null;
    return shipments.find(s => 
      (idOrNumber && s.id === idOrNumber) ||
      (idOrNumber && s.poId === idOrNumber) ||
      (idOrNumber && s.poNumber === idOrNumber) ||
      (fallbackPoNum && s.poNumber === fallbackPoNum) ||
      (fallbackPoNum && s.poId === fallbackPoNum)
    ) || null;
  };

  const { zones } = useZones();
  const { buildings } = useBuildings();

  const loading = invLoading;
  // Derived state from hooks
  const logisticsMap = {};
  const shipMap = {};
  shipments.forEach(shipment => {
    if (shipment.poId) {
      logisticsMap[shipment.poId] = shipment.status;
      shipMap[shipment.poId] = shipment;
    }
  });

  const warehousePos = pos.filter(r => {
    const shipment = shipMap[r.id];
    if (!shipment) return false;
    if (shipment.status === "Warehouse") return true;
    const itemStatuses = shipment.itemStatuses || {};
    return Object.values(itemStatuses).some(item => {
      const status = typeof item === "object" ? item?.status : item;
      return status === "Warehouse";
    });
  });

  const [selectedPoItems, setSelectedPoItems] = useState([]);
  const [loadingPoItems, setLoadingPoItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [companyFilter, setCompanyFilter] = useState("All");

  const getPoLogoUrl = (po) => {
    if (po?.logoUrl) return po.logoUrl;
    const activeLogo = companyLogos.find(l => l.isActive);
    return activeLogo?.url || "";
  };

  const getCompanyNameForPo = (po) => {
    if (!po?.logoUrl) {
      const activeLogo = companyLogos.find(l => l.isActive);
      return activeLogo?.name || "No Company";
    }
    const logo = companyLogos.find(l => l.url === po.logoUrl);
    return logo?.name || "Unknown Company";
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedNotesItem, setSelectedNotesItem] = useState(null);
  const [tempNotesText, setTempNotesText] = useState("");

  const [zoneModalOpen, setZoneModalOpen] = useState(false);
  const [selectedZoneItem, setSelectedZoneItem] = useState(null);
  const [savingZone, setSavingZone] = useState(false);

  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [savingStock, setSavingStock] = useState(false);

  // Shipping  // Markup Modal States
  const [markupModalOpen, setMarkupModalOpen] = useState(false);
  const [markupPo, setMarkupPo] = useState(null);
  const [tempMarkupRate, setTempMarkupRate] = useState(15);
  const [statusFilter, setStatusFilter] = useState("All");

  const handleGeneratePackingSlip = (shipment, batch = null, batchIndex = null, totalBatches = 1) => {
    const po = pos.find(p => p.id === shipment?.poId || p.poNumber === shipment?.poNumber);
    const activeLogoUrl = getPoLogoUrl(po);
    printPackingSlip({
      shipment,
      po,
      batch,
      batchIndex,
      totalBatches,
      companyLogoUrl: activeLogoUrl
    });
  };

  // Shipping to Client modal states
  const [shipModalOpen, setShipModalOpen] = useState(false);
  const [isPartialShipment, setIsPartialShipment] = useState(false);
  const [shipPo, setShipPo] = useState(null);
  const [shipmentToUpdate, setShipmentToUpdate] = useState(null);
  const [loadingShipment, setLoadingShipment] = useState(false);
  const [savingShipment, setSavingShipment] = useState(false);
  const [shipChecklist, setShipChecklist] = useState({});
  const [shipShowPartialInput, setShipShowPartialInput] = useState({});
  const [shipShowFlagInput, setShipShowFlagInput] = useState({});
  const [shipGeneralNotes, setShipGeneralNotes] = useState("");
  const [actualShippingCost, setActualShippingCost] = useState("");

  // Edit Shipping Cost Modal states
  const [editCostModalOpen, setEditCostModalOpen] = useState(false);
  const [editCostContext, setEditCostContext] = useState(null);

  // Address editing states inside Ship to Client modal
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [editAddressData, setEditAddressData] = useState({
    shipToAddress: "",
    shipToAddress2: "",
    shipToCity: "",
    shipToState: "",
    shipToZip: ""
  });
  
  // Accordion collapsed state for PO groups
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [localEdits, setLocalEdits] = useState({});

  // Form state
  const [formData, setFormData] = useState({
    sku: "",
    name: "",
    category: "Raw Materials",
    quantity: 0,
    orderCount: 0,
    warehouse: "NC",
    poId: "",
    poNumber: "",
    clientName: "",
    poItemId: ""
  });

  const categories = ["Raw Materials", "Electronics", "Equipment", "Packaging", "Spares", "Safety Gear"];
  
  const dynamicZones = zones.filter(z => !z.isOutline).map(z => {
    const defaultB = buildings.find(b => b.name === "Main Warehouse") || (buildings.length > 0 ? buildings[0] : null);
    const b = buildings.find(b => b.id === z.buildingId) || defaultB;
    const bName = b ? b.name : "Building";
    return `${bName}: ${z.name}`;
  });
  
  const warehouses = dynamicZones.length > 0 ? dynamicZones : ["Unassigned"];



  
  const handleOpenAddModal = () => {
    setFormData({
      sku: `INV-${Math.floor(10000 + Math.random() * 90000)}`,
      lineNumber: "",
      name: "",
      category: "Raw Materials",
      quantity: 0,
      orderCount: 0,
      warehouse: "Main DC",
      zone: "",
      poId: "",
      poNumber: "",
      clientName: "",
      poItemId: ""
    });
    setSelectedPoItems([]);
    setModalOpen(true);
  };

  const handlePoSelect = (poId) => {
    const selectedPo = pos.find(p => p.id === poId);
    setFormData(prev => ({
      ...prev,
      poId,
      poNumber: selectedPo?.poNumber || "",
      clientName: selectedPo?.clientName || "",
      poItemId: "",
      lineNumber: ""
    }));
    if (selectedPo && selectedPo.items) {
      setSelectedPoItems(selectedPo.items);
    } else {
      setSelectedPoItems([]);
    }
  };

  const handlePoItemSelect = (itemId) => {
    const item = selectedPoItems.find(i => i.id === itemId);
    if (item) {
      setFormData(prev => ({
        ...prev,
        poItemId: itemId,
        name: item.description,
        orderCount: item.quantity,
        lineNumber: item.lineNumber || ""
      }));
    } else {
      setFormData(prev => ({ ...prev, poItemId: itemId }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addInventoryItem(formData);
      setModalOpen(false);
    } catch (error) {
      console.error("Error adding inventory item", error);
    }
  };

  const handleUpdateFieldLocal = (itemId, field, value) => {
    setLocalEdits(prev => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [field]: value }
    }));
  };

  const handleSaveField = async (itemId, field, directValue) => {
    const item = inventory.find(i => i.id === itemId);
    if (!item) return;
    
    let valToSave = directValue;
    if (valToSave === undefined && localEdits[itemId] && localEdits[itemId][field] !== undefined) {
      valToSave = localEdits[itemId][field];
    }
    
    if (valToSave === undefined || valToSave === item[field]) return; // No change
    
    try {
      await updateInventoryItem(itemId, { [field]: valToSave });
      // clear local edit after save
      setLocalEdits(prev => {
        const newEdits = { ...prev };
        if (newEdits[itemId]) {
          delete newEdits[itemId][field];
        }
        return newEdits;
      });
    } catch (err) {
      console.error("Error updating field", err);
    }
  };

  const handleOpenNotesModal = (item) => {
    setSelectedNotesItem(item);
    setTempNotesText("");
    setNotesModalOpen(true);
  };

  const handleSaveNotes = async (e) => {
    e.preventDefault();
    if (!selectedNotesItem) return;
    try {
      if (tempNotesText.trim()) {
        await appendInventoryLog(selectedNotesItem.id, {
          text: tempNotesText,
          type: "manual",
          author: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown'
        });
      }
      setNotesModalOpen(false);
      setSelectedNotesItem(null);
    } catch (error) {
      console.error("Error saving notes", error);
    }
  };

  const handleOpenZoneModal = (item) => {
    setSelectedZoneItem(item);
    setZoneModalOpen(true);
  };

  const handleSaveZone = async (itemId, newZone) => {
    setSavingZone(true);
    try {
      await updateInventoryItem(itemId, { zone: newZone });
      await appendInventoryLog(itemId, {
        text: `Zone location updated to: ${newZone || "Unassigned"}`,
        type: "Zone",
        author: currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User'
      });
      setZoneModalOpen(false);
      setSelectedZoneItem(null);
    } catch (error) {
      console.error("Error saving zone location", error);
    } finally {
      setSavingZone(false);
    }
  };

  const handleOpenStockModal = (item) => {
    setSelectedStockItem(item);
    setStockModalOpen(true);
  };

  const handleSaveStock = async (itemId, newQty, reason) => {
    const item = inventory.find((i) => i.id === itemId);
    if (!item) return;
    const oldQty = item.quantity;
    setSavingStock(true);
    try {
      await updateInventoryItem(itemId, { quantity: Number(newQty) });
      const logText = `Stock count adjusted from ${oldQty} to ${newQty}${reason ? ` (Reason: ${reason})` : ""}`;
      await appendInventoryLog(itemId, {
        text: logText,
        type: "Stock Adjustment",
        author: currentUser?.displayName || currentUser?.email?.split("@")[0] || "User"
      });
      setStockModalOpen(false);
      setSelectedStockItem(null);
    } catch (error) {
      console.error("Error saving stock count", error);
    } finally {
      setSavingStock(false);
    }
  };

  const handleOpenShipModal = (group, isPartial = false) => {
    setIsPartialShipment(isPartial);
    const poObj = findPo(group.poId, group.poNumber);
    const shipment = findShipment(group.poId, group.poNumber) || (group.poId ? shipMap[group.poId] : null);
    setShipmentToUpdate(shipment || null);
    
    const clientName = group.clientName || poObj?.clientName || shipment?.clientName || "";
    const customerObj = (customers || []).find(c => 
      c.name && clientName && c.name.trim().toLowerCase() === clientName.trim().toLowerCase()
    );

    // Prioritize saved PO address, then saved Shipment address, then group, then customer default
    const resolvedAddress = poObj?.shipToAddress || shipment?.shipToAddress || group.shipToAddress || customerObj?.shipAddress || customerObj?.address || "";
    const resolvedAddress2 = poObj?.shipToAddress2 || shipment?.shipToAddress2 || group.shipToAddress2 || customerObj?.shipAddress2 || customerObj?.address2 || "";
    const resolvedCity = poObj?.shipToCity || shipment?.shipToCity || group.shipToCity || customerObj?.shipCity || customerObj?.city || "";
    const resolvedState = poObj?.shipToState || shipment?.shipToState || group.shipToState || customerObj?.shipState || customerObj?.state || "";
    const resolvedZip = poObj?.shipToZip || shipment?.shipToZip || group.shipToZip || customerObj?.shipZip || customerObj?.zip || "";

    const defaultCarrier = shipment?.carrier || (carriers.length > 0 ? carriers[0] : "FedEx Freight");
    const defaultTracking = shipment?.trackingNumber || "";

    // Set enriched shipPo object so modal header displays customer address
    setShipPo({
      ...group,
      poId: poObj?.id || group.poId,
      poNumber: poObj?.poNumber || group.poNumber,
      clientName: clientName,
      shipToAddress: resolvedAddress,
      shipToAddress2: resolvedAddress2,
      shipToCity: resolvedCity,
      shipToState: resolvedState,
      shipToZip: resolvedZip
    });

    // Initialize address data including carrier and tracking details
    setEditAddressData({
      shipToAddress: resolvedAddress,
      shipToAddress2: resolvedAddress2,
      shipToCity: resolvedCity,
      shipToState: resolvedState,
      shipToZip: resolvedZip,
      carrier: defaultCarrier,
      trackingNumber: defaultTracking
    });

    // Initialize checklist
    const initialChecklist = {};
    const items = group.items || [];
    items.forEach(item => {
      const itemStatus = shipment?.itemStatuses?.[item.poItemId]?.status || shipment?.itemStatuses?.[item.id]?.status;
      const alreadyShipped = itemStatus === "Transport -> Client" || itemStatus === "Delivered";

      initialChecklist[item.id] = {
        arrived: alreadyShipped ? true : !isPartial,
        receivedQuantity: item.quantity,
        isFlagged: false,
        flagReason: "",
        notes: "",
        poItemId: item.poItemId || "",
        alreadyShipped: alreadyShipped
      };
    });
    setShipChecklist(initialChecklist);
    setShipGeneralNotes("");
    setActualShippingCost("");
    setIsEditingAddress(false);
    setShipModalOpen(true);
  };

  const handleShipSubmit = async (e) => {
    e.preventDefault();
    setSavingShipment(true);
    try {
      const defaultDest = [
        shipPo?.shipToAddress,
        shipPo?.shipToAddress2,
        shipPo?.shipToCity,
        shipPo?.shipToState,
        shipPo?.shipToZip
      ].filter(Boolean).join(", ") || "Client Address";

      const data = {
        status: "Client",
        destination: editAddressData.shipToAddress
          ? [editAddressData.shipToAddress, editAddressData.shipToAddress2, editAddressData.shipToCity, editAddressData.shipToState, editAddressData.shipToZip].filter(Boolean).join(", ")
          : defaultDest,
        carrier: editAddressData.carrier || shipmentToUpdate?.carrier || "N/A",
        trackingNumber: editAddressData.trackingNumber || shipmentToUpdate?.trackingNumber || "N/A",
        shippedAt: new Date().toISOString()
      };

      const inventoryUpdatePromises = [];

      if (shipmentToUpdate) {
        const updatedItemStatuses = { ...(shipmentToUpdate.itemStatuses || {}) };
        
        // Normalize any string statuses to objects to avoid property setting type errors
        Object.keys(updatedItemStatuses).forEach(key => {
          if (typeof updatedItemStatuses[key] === "string") {
            updatedItemStatuses[key] = { status: updatedItemStatuses[key] };
          }
        });

        // Track items shipped in the current batch
        const currentBatchItems = [];

        const matchedPo = pos.find(p => p.id === shipPo.poId || p.poNumber === shipPo.poNumber);
        const poItems = matchedPo ? [...(matchedPo.items || [])] : [];
        let poModified = false;
        const newPoItems = [];

        // Loop over the items of the PO to process splitting and shipping
        poItems.forEach(poItem => {
          // Find corresponding warehouse inventory items
          const invDocs = (shipPo.items || []).filter(i => 
            i.poItemId === poItem.id || 
            (i.poItemId && i.poItemId.startsWith(`${poItem.id}_`)) ||
            i.id === poItem.id ||
            (!i.poItemId && i.name && poItem.description && (i.name === poItem.description || i.name.startsWith(poItem.description)))
          );
          
          let totalShippedForThisPoItem = 0;
          let totalRemainingForThisPoItem = 0;
          let hasPartialShip = false;
          
          if (invDocs.length > 0) {
            invDocs.forEach(invDoc => {
              const check = shipChecklist[invDoc.id];
              if (check) {
                const shouldShip = isPartialShipment ? check.arrived : true;
                if (shouldShip && !check.alreadyShipped) {
                  const qtyShipped = Number(check.receivedQuantity || 0);
                  const currentStock = Number(invDoc.quantity || 0);
                  const qtyRemaining = Math.max(0, currentStock - qtyShipped);
                  
                  totalShippedForThisPoItem += qtyShipped;
                  totalRemainingForThisPoItem += qtyRemaining;
                  
                  if (qtyRemaining > 0 && qtyShipped > 0) {
                    hasPartialShip = true;
                  }
                } else if (check.alreadyShipped) {
                  // already shipped
                } else {
                  totalRemainingForThisPoItem += Number(invDoc.quantity || 0);
                }
              } else {
                totalRemainingForThisPoItem += Number(invDoc.quantity || 0);
              }
            });
          }

          if (hasPartialShip && totalShippedForThisPoItem > 0) {
            poModified = true;
            
            // 1. Create unique ID for new split PO item
            const newSplitItemId = `li-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
            const shippedSplitItem = {
              ...poItem,
              id: newSplitItemId,
              lineNumber: poItem.lineNumber ? `${poItem.lineNumber}.1` : "",
              quantity: totalShippedForThisPoItem
            };

            // 2. Adjust original PO item's quantity
            const remainingPoItem = {
              ...poItem,
              quantity: poItem.quantity - totalShippedForThisPoItem
            };

            newPoItems.push(remainingPoItem);
            newPoItems.push(shippedSplitItem);

            // 3. Update logistics status map
            updatedItemStatuses[poItem.id] = {
              ...(updatedItemStatuses[poItem.id] || {}),
              status: "Warehouse"
            };
            updatedItemStatuses[newSplitItemId] = {
              status: "Transport -> Client",
              orderedDate: updatedItemStatuses[poItem.id]?.orderedDate || new Date().toISOString().split("T")[0],
              shipDate: new Date().toISOString().split("T")[0],
              carrier: data.carrier,
              trackingNumber: data.trackingNumber
            };

            // 4. Update the batch tracking
            invDocs.forEach(invDoc => {
              const check = shipChecklist[invDoc.id];
              if (check && check.arrived && !check.alreadyShipped) {
                const qtyShipped = Number(check.receivedQuantity || 0);
                if (qtyShipped > 0) {
                  currentBatchItems.push({
                    id: invDoc.id,
                    poItemId: newSplitItemId,
                    lineNumber: shippedSplitItem.lineNumber || poItem.lineNumber || invDoc.lineNumber || "",
                    name: invDoc.name || poItem.description || "Item",
                    description: poItem.description || invDoc.description || "",
                    quantity: qtyShipped
                  });
                }
              }
            });

          } else {
            // Normal case (No split needed for this PO item because it is either all shipped or none shipped)
            newPoItems.push(poItem);

            if (invDocs.length > 0) {
              invDocs.forEach(invDoc => {
                const check = shipChecklist[invDoc.id];
                if (check) {
                  const shouldShip = isPartialShipment ? check.arrived : true;
                  if (shouldShip && !check.alreadyShipped) {
                    const qtyShipped = Number(check.receivedQuantity || 0);
                    const currentStock = Number(invDoc.quantity || 0);
                    const isFullyShipped = qtyShipped >= currentStock && qtyShipped > 0;

                    if (isFullyShipped || qtyShipped > 0) {
                      updatedItemStatuses[poItem.id] = {
                        ...(updatedItemStatuses[poItem.id] || {}),
                        status: "Transport -> Client",
                        shipDate: new Date().toISOString().split("T")[0],
                        carrier: data.carrier,
                        trackingNumber: data.trackingNumber
                      };
                      if (invDoc.poItemId && invDoc.poItemId !== poItem.id) {
                        updatedItemStatuses[invDoc.poItemId] = {
                          ...(updatedItemStatuses[invDoc.poItemId] || {}),
                          status: "Transport -> Client",
                          shipDate: new Date().toISOString().split("T")[0],
                          carrier: data.carrier,
                          trackingNumber: data.trackingNumber
                        };
                      }

                      currentBatchItems.push({
                        id: invDoc.id,
                        poItemId: poItem.id,
                        lineNumber: poItem.lineNumber || invDoc.lineNumber || "",
                        name: invDoc.name || poItem.description || "Item",
                        description: poItem.description || invDoc.description || "",
                        quantity: qtyShipped
                      });
                    }
                  }
                }
              });
            }
          }
        });

        // Check any inventory items in shipPo that weren't matched in poItems
        (shipPo.items || []).forEach(invDoc => {
          const check = shipChecklist[invDoc.id];
          if (check && (isPartialShipment ? check.arrived : true) && !check.alreadyShipped) {
            const qtyShipped = Number(check.receivedQuantity || 0);
            if (qtyShipped > 0 && !currentBatchItems.some(bi => bi.id === invDoc.id)) {
              const targetKey = invDoc.poItemId || invDoc.id;
              currentBatchItems.push({
                id: invDoc.id,
                poItemId: targetKey,
                lineNumber: invDoc.lineNumber || "",
                name: invDoc.name || "Item",
                description: invDoc.description || "",
                quantity: qtyShipped
              });
              updatedItemStatuses[targetKey] = {
                ...(updatedItemStatuses[targetKey] || {}),
                status: "Transport -> Client",
                shipDate: new Date().toISOString().split("T")[0],
                carrier: data.carrier,
                trackingNumber: data.trackingNumber
              };
            }
          }
        });

        // Save PO changes to Firestore (items split and/or updated address)
        const targetPo = matchedPo || findPo(shipPo?.poId, shipPo?.poNumber);
        const poUpdatePayload = {
          ...(poModified ? { items: newPoItems } : {}),
          ...(editAddressData.shipToAddress ? {
            shipToAddress: editAddressData.shipToAddress || "",
            shipToAddress2: editAddressData.shipToAddress2 || "",
            shipToCity: editAddressData.shipToCity || "",
            shipToState: editAddressData.shipToState || "",
            shipToZip: editAddressData.shipToZip || ""
          } : {})
        };

        if (targetPo?.id && (poModified || editAddressData.shipToAddress)) {
          await updatePO(targetPo.id, poUpdatePayload);
        }

        // Update shipmentsHistory array
        const costVal = parseFloat(actualShippingCost) || 0;
        const shipmentsHistory = [...(shipmentToUpdate.shipmentsHistory || [])];
        let newBatch = null;
        if (currentBatchItems.length > 0) {
          newBatch = {
            id: `batch-${shipmentsHistory.length + 1}-${Date.now()}`,
            shippedAt: new Date().toISOString(),
            carrier: data.carrier,
            trackingNumber: data.trackingNumber,
            destination: data.destination,
            notes: shipGeneralNotes || "",
            actualShippingCost: costVal,
            items: currentBatchItems
          };
          shipmentsHistory.push(newBatch);
        }

        // Determine if there are still unshipped items in the entire PO
        const allItemsToCheck = newPoItems.length > 0 ? newPoItems : (shipPo.items || []);
        const hasUnshippedItems = allItemsToCheck.some(item => {
          const itemStatObj = updatedItemStatuses[item.id] || 
            Object.entries(updatedItemStatuses).find(([k]) => k === item.id || k.startsWith(item.id + "_"))?.[1];
          const status = (typeof itemStatObj === "object" ? itemStatObj?.status : itemStatObj) || shipmentToUpdate.status || "To Order";
          return status === "Warehouse" || status === "To Order" || status === "Purchased" || status === "Transport -> NC" || status === "Transport -> HQ";
        });

        const nextShipmentStatus = hasUnshippedItems ? "Warehouse" : "Client";
        const currentShipCost = parseFloat(shipmentToUpdate.actualShippingCost) || 0;

        const updatedData = {
          ...data,
          status: nextShipmentStatus,
          actualShippingCost: currentShipCost + costVal,
          itemStatuses: updatedItemStatuses,
          shipmentsHistory: shipmentsHistory
        };
        await updateShipment(shipmentToUpdate.id, updatedData);

        if (costVal > 0) {
          logSecurityEvent({
            action: "SHIPMENT_COST_RECORDED",
            category: "DATA_MODIFICATION",
            userId: currentUser?.uid || "anonymous",
            userEmail: currentUser?.email || "anonymous",
            resource: `logistics/${shipmentToUpdate.id}`,
            status: "SUCCESS",
            details: {
              poNumber: shipmentToUpdate.poNumber,
              batchIndex: shipmentsHistory.length - 1,
              actualShippingCost: costVal
            }
          });
        }

        // Auto-print packing slip for the new batch if created, otherwise print overall
        if (newBatch) {
          const batchIndex = shipmentsHistory.length - 1;
          handleGeneratePackingSlip({
            ...shipmentToUpdate,
            ...updatedData
          }, newBatch, batchIndex, shipmentsHistory.length);
        } else {
          handleGeneratePackingSlip({
            ...shipmentToUpdate,
            ...updatedData
          });
        }
      }

      // Automatically append logs to each inventory item in the checklist that was shipped in this batch
      const shipPromises = [];
      for (const itemId of Object.keys(shipChecklist)) {
        const check = shipChecklist[itemId];
        if (!check.arrived || check.alreadyShipped) continue;

        const logParts = [];
        logParts.push(`Dispatched for Shipping.`);
        logParts.push(`Qty: ${check.receivedQuantity}`);
        if (check.isFlagged) {
          logParts.push(`Flag: ${check.flagReason}`);
        }
        if (check.notes) {
          logParts.push(`Notes: ${check.notes}`);
        }
        if (shipGeneralNotes) {
          logParts.push(`General: ${shipGeneralNotes}`);
        }
        
        shipPromises.push(appendInventoryLog(itemId, {
          text: logParts.join(" | "),
          type: "shipping"
        }));
      }

      await Promise.all([
        ...inventoryUpdatePromises,
        ...shipPromises
      ]);

      setShipModalOpen(false);
    } catch (error) {
      console.error("Error shipping to client", error);
      alert("Error shipping to client: " + error.message);
    } finally {
      setSavingShipment(false);
    }
  };

  const handleOpenCostModal = (shipment, batch, batchIndex, totalBatches) => {
    setEditCostContext({
      shipment,
      batch,
      batchIndex,
      totalBatches
    });
    setEditCostModalOpen(true);
  };

  const handleSaveShipmentCost = async (newCost) => {
    if (!editCostContext || !editCostContext.shipment) return;
    const { shipment, batch, batchIndex } = editCostContext;
    const numCost = parseFloat(newCost) || 0;

    let updatedHistory = shipment.shipmentsHistory ? [...shipment.shipmentsHistory] : [];
    let updatedShipmentCost = parseFloat(shipment.actualShippingCost) || 0;

    if (batch && batchIndex !== undefined && batchIndex !== null && updatedHistory[batchIndex]) {
      updatedHistory[batchIndex] = {
        ...updatedHistory[batchIndex],
        actualShippingCost: numCost
      };
      updatedShipmentCost = updatedHistory.reduce((sum, b) => sum + (parseFloat(b.actualShippingCost) || 0), 0);
    } else {
      updatedShipmentCost = numCost;
      if (updatedHistory.length === 1) {
        updatedHistory[0] = {
          ...updatedHistory[0],
          actualShippingCost: numCost
        };
      }
    }

    await updateShipment(shipment.id, {
      actualShippingCost: updatedShipmentCost,
      shipmentsHistory: updatedHistory
    });

    try {
      await logSecurityEvent({
        action: "SHIPMENT_COST_UPDATED",
        category: "DATA_MODIFICATION",
        userId: currentUser?.uid || "anonymous",
        userEmail: currentUser?.email || "anonymous",
        resource: `logistics/${shipment.id}`,
        status: "SUCCESS",
        details: {
          poNumber: shipment.poNumber,
          batchIndex,
          newCost: numCost
        }
      });
    } catch (logErr) {
      console.error("Failed to log shipping cost update:", logErr);
    }
  };

  const handleSaveAddress = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setSavingAddress(true);
    try {
      const addressUpdates = {
        shipToAddress: editAddressData.shipToAddress || "",
        shipToAddress2: editAddressData.shipToAddress2 || "",
        shipToCity: editAddressData.shipToCity || "",
        shipToState: editAddressData.shipToState || "",
        shipToZip: editAddressData.shipToZip || ""
      };

      const matchedPo = findPo(shipPo?.poId, shipPo?.poNumber);
      const matchedShipment = shipmentToUpdate || findShipment(shipPo?.poId, shipPo?.poNumber);

      const promises = [];
      if (matchedPo?.id) {
        promises.push(updatePO(matchedPo.id, addressUpdates));
      }

      const formattedDest = [
        editAddressData.shipToAddress,
        editAddressData.shipToAddress2,
        editAddressData.shipToCity,
        editAddressData.shipToState,
        editAddressData.shipToZip
      ].filter(Boolean).join(", ");

      const logisticsUpdates = {
        ...(formattedDest ? { destination: formattedDest } : {}),
        shipToAddress: editAddressData.shipToAddress || "",
        shipToAddress2: editAddressData.shipToAddress2 || "",
        shipToCity: editAddressData.shipToCity || "",
        shipToState: editAddressData.shipToState || "",
        shipToZip: editAddressData.shipToZip || "",
        carrier: editAddressData.carrier || matchedShipment?.carrier || "",
        trackingNumber: editAddressData.trackingNumber || matchedShipment?.trackingNumber || ""
      };

      if (matchedShipment?.id) {
        promises.push(updateShipment(matchedShipment.id, logisticsUpdates));
      }

      await Promise.all(promises);

      // Update local state so UI immediately reflects changes
      setShipPo(prev => (prev ? {
        ...prev,
        ...addressUpdates
      } : prev));

      if (matchedShipment) {
        setShipmentToUpdate(prev => (prev ? {
          ...prev,
          ...logisticsUpdates
        } : matchedShipment));
      }

      setIsEditingAddress(false);
    } catch (error) {
      console.error("Error saving shipping address:", error);
      alert("Error saving shipping address: " + error.message);
    } finally {
      setSavingAddress(false);
    }
  };

  const handleShipItemQtyChange = (itemId, val) => {
    setShipChecklist(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], receivedQuantity: val }
    }));
  };

  const handleShipItemFlagReasonChange = (itemId, val) => {
    setShipChecklist(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], flagReason: val }
    }));
  };

  const handleShipItemNotesChange = (itemId, val) => {
    setShipChecklist(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes: val }
    }));
  };

  const toggleShipArrived = (itemId) => {
    setShipChecklist(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], arrived: !prev[itemId]?.arrived }
    }));
  };

  const toggleShipFlagged = (itemId) => {
    setShipChecklist(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], isFlagged: !prev[itemId]?.isFlagged }
    }));
  };

  const toggleShipPartialInput = (itemId) => {
    setShipShowPartialInput(prev => {
      const nextShow = !prev[itemId];
      if (!nextShow) {
        setShipChecklist(prevCheck => {
          const invItem = (shipPo?.items || []).find(i => i.id === itemId);
          const fullQty = invItem ? invItem.quantity : 0;
          return {
            ...prevCheck,
            [itemId]: { ...prevCheck[itemId], receivedQuantity: fullQty }
          };
        });
      }
      return {
        ...prev,
        [itemId]: nextShow
      };
    });
  };

  const filteredInventory = inventory.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (item.lineNumber && String(item.lineNumber).toLowerCase().includes(searchTerm.toLowerCase())) ||
                          item.warehouse.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (item.zone && item.zone.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (item.poNumber && item.poNumber.toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (!matchesSearch) return false;

    // Filter by company assignment
    const poObj = (item.poId || item.poNumber)
      ? pos.find(p => p.id === item.poId || p.poNumber === item.poNumber)
      : null;

    if (poObj) {
      const matchesCompanyAssignment = (() => {
        const poLogoUrl = poObj.logoUrl || companyLogos.find(l => l.isActive)?.url || "";
        return companyLogos.some(logo => logo.url === poLogoUrl);
      })();
      if (!matchesCompanyAssignment) return false;
    }

    if (companyFilter !== "All") {
      if (!poObj) return false;
      const companyName = getCompanyNameForPo(poObj);
      if (companyName !== companyFilter) return false;
    }

    // Show manual items (no poId) if they have quantity in stock
    if (!item.poId) {
      return item.quantity > 0;
    }

    // If PO is fully delivered or shipment status is Delivered, remove from Inventory
    if (poObj && isPoFullyDelivered(poObj)) {
      return false;
    }

    const shipment = shipMap[item.poId];
    if (shipment) {
      if (shipment.status === "Delivered") {
        return false;
      }
      if (item.poItemId && shipment.itemStatuses) {
        const itemStatusObj = shipment.itemStatuses[item.poItemId];
        const itemStatus = typeof itemStatusObj === "object" ? itemStatusObj?.status : itemStatusObj;
        if (itemStatus === "Delivered") {
          return false;
        }
      }
    }

    // Show PO items if they are in stock (quantity > 0) or if the shipment is currently in Warehouse status
    if (item.quantity > 0) return true;
    if (shipment && shipment.status === "Warehouse") return true;

    return false;
  });

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Inventory Tracking</h1>
          <p>Verify Warehouse Content</p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenAddModal}>
          <Plus size={18} />
          Add SKU Item
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="table-actions-container" style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
        <div className="search-box-container" style={{ flex: 1 }}>
          <Search size={18} className="search-icon" />
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search inventory by Line #, SKU, name, warehouse, or PO..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ margin: 0 }}
          />
        </div>
        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          className="form-input"
          style={{ width: "220px", height: "42px", margin: 0, padding: "0.25rem 0.5rem", backgroundColor: "var(--bg-card)" }}
        >
          <option value="All">All Companies</option>
          {companyLogos.map(logo => (
            <option key={logo.id} value={logo.name}>{logo.name}</option>
          ))}
        </select>
      </div>

      {/* Inventory Spreadsheet-like Table grouped by PO */}
      {loading && inventory.length === 0 ? (
        <div className="loading-container">
          <Loader2 className="spinner-icon" size={36} />
          <span>Scanning barcode records...</span>
        </div>
      ) : filteredInventory.length === 0 ? (
        <div className="empty-state-card">
          <Package size={48} className="empty-state-icon" />
          <h3>No Inventory Items Found</h3>
          <p>Verify that your logistics shipments have reached the "Warehouse" status.</p>
        </div>
      ) : (() => {
        // Group inventory items by PO
        const grouped = {};
        filteredInventory.forEach(item => {
          const matchedPoForGroup = findPo(item.poId, item.poNumber);
          const matchedShipmentForGroup = findShipment(item.poId, item.poNumber);
          const key = matchedPoForGroup?.id || item.poId || item.poNumber || "unassigned";

          if (!grouped[key]) {
            grouped[key] = {
              poId: matchedPoForGroup?.id || item.poId || "",
              poNumber: matchedPoForGroup?.poNumber || item.poNumber || "Unassigned PO",
              clientName: item.clientName || matchedPoForGroup?.clientName || matchedShipmentForGroup?.clientName || "",
              shipToAddress: matchedPoForGroup?.shipToAddress || matchedShipmentForGroup?.shipToAddress || "",
              shipToAddress2: matchedPoForGroup?.shipToAddress2 || matchedShipmentForGroup?.shipToAddress2 || "",
              shipToCity: matchedPoForGroup?.shipToCity || matchedShipmentForGroup?.shipToCity || "",
              shipToState: matchedPoForGroup?.shipToState || matchedShipmentForGroup?.shipToState || "",
              shipToZip: matchedPoForGroup?.shipToZip || matchedShipmentForGroup?.shipToZip || "",
              items: []
            };
          }
          grouped[key].items.push(item);
        });

        return Object.keys(grouped).map(poKey => {
          const group = grouped[poKey];
          const isCollapsed = collapsedGroups[poKey] === true;
          const hasMismatch = group.items.some(item => Number(item.quantity) !== Number(item.orderCount));
          const poObj = findPo(group.poId, group.poNumber);
          const logoUrl = getPoLogoUrl(poObj);
          const linkedShipment = findShipment(group.poId, group.poNumber);
          return (
            <div key={poKey} className="po-inventory-group" style={{ marginBottom: "2.5rem" }}>
              <div 
                className="po-group-header" 
                onClick={() => setCollapsedGroups(prev => ({ ...prev, [poKey]: !prev[poKey] }))}
                data-collapsed={isCollapsed}
              >
                <div className="po-group-header-left">
                  {isCollapsed ? <ChevronDown size={16} color="var(--text-muted)" style={{ marginRight: "0.25rem", flexShrink: 0 }} /> : <ChevronUp size={16} color="var(--text-muted)" style={{ marginRight: "0.25rem", flexShrink: 0 }} />}
                  {logoUrl && (
                    <img 
                      src={logoUrl} 
                      alt="Company logo" 
                      style={{ width: "20px", height: "20px", borderRadius: "4px", objectFit: "cover", marginRight: "0.5rem", flexShrink: 0 }} 
                    />
                  )}
                  <span style={{ fontWeight: 700, color: "var(--primary)", fontSize: "0.95rem", display: "inline-flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap", lineHeight: 1.4 }}>
                    PO: <span style={{ color: "var(--text-primary)" }}>{group.poNumber}</span>
                    {(poObj?.customerPoNumber || group.customerPoNumber) && (
                      <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                        • Cust PO: <strong style={{ color: "var(--text-primary)" }}>{poObj?.customerPoNumber || group.customerPoNumber}</strong>
                      </span>
                    )}
                    {poObj?.isPriority && <span className="priority-pill">PRIORITY</span>}
                    {group.clientName ? `— Client: ${group.clientName}` : ""}
                  </span>
                </div>
                <div className="po-group-header-right">
                  <span 
                    className="sku-badge" 
                    style={{ 
                      fontSize: "0.75rem", 
                      backgroundColor: "rgba(33, 150, 243, 0.1)", 
                      color: "var(--primary)",
                      margin: 0
                    }}
                  >
                    {group.items.length} Item(s) in Warehouse
                  </span>
                  {poObj && (
                    <button
                      type="button"
                      className="btn btn-warning"
                      disabled={isPoFullyDelivered(poObj)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedChangeRequestPo(poObj);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        padding: "0.3rem 0.6rem",
                        fontSize: "0.8rem",
                        minHeight: "auto",
                        color: "#000000",
                        opacity: isPoFullyDelivered(poObj) ? 0.4 : 1,
                        cursor: isPoFullyDelivered(poObj) ? "not-allowed" : "pointer"
                      }}
                      title={isPoFullyDelivered(poObj) ? "All line items have been delivered. No changes can be made." : "Open Change Request modal"}
                    >
                      <Sliders size={12} color="#000000" />
                      Change Request
                    </button>
                  )}
                  {(() => {
                    const { primaryShipment, batches, totalBatches } = getAllPackingSlipsForPO({ po: poObj, shipment: linkedShipment, shipments });
                    if (!batches || batches.length === 0) return null;
                    return (
                      <>
                        <div style={{ display: "inline-flex", gap: "0.3rem", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                          {batches.map((batch, idx) => (
                            <button
                              key={batch.id || idx}
                              type="button"
                              className="btn btn-secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGeneratePackingSlip(primaryShipment || linkedShipment, batch, idx, totalBatches);
                              }}
                              title={`Print Packing Slip for Shipment #${idx + 1}\nCarrier: ${batch.carrier || "N/A"}\nTracking: ${batch.trackingNumber || "N/A"}\nDate: ${batch.shippedAt ? new Date(batch.shippedAt).toLocaleDateString() : "N/A"}\nItems: ${batch.items?.length || 0}`}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "0.25rem",
                                padding: "0.3rem 0.55rem",
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                minHeight: "auto",
                                borderColor: "rgba(0, 224, 150, 0.4)",
                                color: "#00e096",
                                background: "rgba(0, 224, 150, 0.08)"
                              }}
                            >
                              <FileText size={12} />
                              Slip #{idx + 1}
                            </button>
                          ))}
                        </div>

                        <div style={{ display: "inline-flex", gap: "0.3rem", alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
                          {totalBatches === 1 ? (() => {
                            const cost = batches[0]?.actualShippingCost !== undefined && batches[0]?.actualShippingCost !== null
                              ? Number(batches[0].actualShippingCost)
                              : (Number(primaryShipment?.actualShippingCost) || 0);
                            const costText = cost > 0 ? `$${cost.toFixed(2)}` : "Set Cost";
                            return (
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCostModal(primaryShipment || linkedShipment, batches[0], 0, 1);
                                }}
                                title={`Click to view/edit shipping cost ($${cost.toFixed(2)}) for full shipment`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                  padding: "0.3rem 0.55rem",
                                  fontSize: "0.78rem",
                                  fontWeight: 600,
                                  minHeight: "auto",
                                  borderColor: "rgba(59, 130, 246, 0.4)",
                                  color: "#3b82f6",
                                  background: "rgba(59, 130, 246, 0.08)"
                                }}
                              >
                                <DollarSign size={12} />
                                Ship Cost: {costText}
                              </button>
                            );
                          })() : (
                            batches.map((batch, idx) => {
                              const cost = batch.actualShippingCost !== undefined && batch.actualShippingCost !== null
                                ? Number(batch.actualShippingCost)
                                : 0;
                              const costText = cost > 0 ? `$${cost.toFixed(2)}` : "Set Cost";
                              return (
                                <button
                                  key={batch.id ? `cost-${batch.id}` : `cost-${idx}`}
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenCostModal(primaryShipment || linkedShipment, batch, idx, totalBatches);
                                  }}
                                  title={`Click to view/edit shipping cost ($${cost.toFixed(2)}) for Shipment #${idx + 1}`}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "0.25rem",
                                    padding: "0.3rem 0.55rem",
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    minHeight: "auto",
                                    borderColor: "rgba(59, 130, 246, 0.4)",
                                    color: "#3b82f6",
                                    background: "rgba(59, 130, 246, 0.08)"
                                  }}
                                >
                                  <DollarSign size={12} />
                                  Ship Cost #{idx + 1}: {costText}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </>
                    );
                  })()}
                  {(poObj || group.poNumber) && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTasksPo(poObj || { id: group.items[0]?.poId || group.poNumber, poNumber: group.poNumber, clientName: group.clientName });
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.4rem",
                        padding: "0.3rem 0.6rem",
                        fontSize: "0.8rem",
                        minHeight: "auto"
                      }}
                      title="View & manage tasks for this PO"
                    >
                      <CheckSquare size={12} />
                      Tasks
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenShipModal(group, false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      background: "var(--primary)",
                      borderColor: "var(--primary)",
                      color: "#ffffff",
                      padding: "0.3rem 0.6rem",
                      fontSize: "0.8rem",
                      minHeight: "auto"
                    }}
                  >
                    <Truck size={12} />
                    Ship to Client
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenShipModal(group, true);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      padding: "0.3rem 0.6rem",
                      fontSize: "0.8rem",
                      minHeight: "auto"
                    }}
                  >
                    <Truck size={12} />
                    Ship Partial
                  </button>
                </div>
              </div>
              {!isCollapsed && (
                <div className="table-container" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0, marginTop: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "80px", textAlign: "center" }}>Line #</th>
                      <th>Item Name</th>
                      <th style={{ width: "180px" }}>Notes</th>
                      <th style={{ width: "110px", textAlign: "center" }}>Stock Count</th>
                      <th style={{ width: "150px" }}>Warehouse</th>
                      <th style={{ width: "160px" }}>Zone</th>
                      <th style={{ width: "110px", textAlign: "center" }}>Order Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((item) => {
                      const resolvedLineNumber = item.lineNumber || (() => {
                        if (poObj?.items) {
                          const matched = poObj.items.find(pi => 
                            pi.id === item.poItemId || 
                            (item.poItemId && item.poItemId.startsWith(pi.id + "_")) ||
                            pi.description === item.name ||
                            (item.name && item.name.startsWith(pi.description))
                          );
                          return matched?.lineNumber;
                        }
                        return null;
                      })() || item.sku;

                      return (
                        <tr key={item.id}>
                          <td style={{ textAlign: "center" }}>
                            <span className="sku-badge" style={{ display: "inline-block", fontWeight: 700 }}>
                              {resolvedLineNumber}
                            </span>
                          </td>
                          <td style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                            {item.name}
                          </td>
                          <td>
                            <div
                              onClick={() => handleOpenNotesModal(item)}
                              style={{
                                cursor: "pointer",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                maxWidth: "180px",
                                color: (item.logs && item.logs.length > 0) || item.notes ? "var(--text-primary)" : "var(--text-muted)",
                                fontStyle: (item.logs && item.logs.length > 0) || item.notes ? "normal" : "italic",
                                fontSize: "0.85rem",
                                padding: "0.4rem 0.5rem",
                                border: "1px solid transparent",
                                borderRadius: "4px",
                                backgroundColor: (item.logs && item.logs.length > 0) || item.notes ? "rgba(255, 255, 255, 0.03)" : "transparent"
                              }}
                              title="View / Add Logs"
                            >
                              {item.logs && item.logs.length > 0 ? `${item.logs.length} Log(s)` : (item.notes || "Add log...")}
                            </div>
                          </td>
                        <td style={{ textAlign: "center" }}>
                          <div
                            onClick={() => handleOpenStockModal(item)}
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.25rem",
                              padding: "0.25rem 0.6rem",
                              borderRadius: "6px",
                              fontWeight: Number(item.quantity) !== Number(item.orderCount) ? "700" : "600",
                              fontSize: "0.9rem",
                              backgroundColor: Number(item.quantity) !== Number(item.orderCount)
                                ? "rgba(239, 68, 68, 0.12)"
                                : "rgba(255, 255, 255, 0.03)",
                              color: Number(item.quantity) !== Number(item.orderCount)
                                ? "var(--danger)"
                                : "var(--text-primary)",
                              border: "1px solid " + (Number(item.quantity) !== Number(item.orderCount) ? "rgba(239, 68, 68, 0.3)" : "var(--border-color)"),
                              transition: "all 0.15s ease",
                              minWidth: "45px"
                            }}
                            title="Click to adjust stock count"
                          >
                            <span>{item.quantity}</span>
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const shipment = shipMap[item.poId];
                            const itemStatus = shipment?.itemStatuses?.[item.poItemId]?.status || shipment?.status || "Warehouse";
                            if (shipment && itemStatus !== "Warehouse") {
                              return (
                                <span 
                                  className="log-item-status-badge" 
                                  style={{ 
                                    display: "inline-block", 
                                    padding: "0.2rem 0.4rem", 
                                    fontWeight: 600,
                                    borderRadius: "4px",
                                    fontSize: "0.85rem",
                                    color: itemStatus === "Transport -> Client" ? "var(--warning)" : "var(--success)" 
                                  }}
                                >
                                  {itemStatus}
                                </span>
                              );
                            }
                            return (
                              <select
                                className="inline-edit-input"
                                value={item.warehouse || "NC"}
                                onChange={(e) => {
                                  handleUpdateFieldLocal(item.id, "warehouse", e.target.value);
                                  handleSaveField(item.id, "warehouse", e.target.value);
                                }}
                                style={{ color: "var(--text-primary)", border: "1px solid transparent", cursor: "pointer", backgroundColor: "transparent" }}
                              >
                                {warehouses.map(wh => (
                                  <option key={wh} value={wh} style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-card)" }}>
                                    {wh}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}
                        </td>
                        <td>
                          <div
                            onClick={() => handleOpenZoneModal(item)}
                            style={{
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.35rem",
                              padding: "0.35rem 0.6rem",
                              borderRadius: "6px",
                              fontSize: "0.85rem",
                              fontWeight: 500,
                              backgroundColor: item.zone ? "rgba(59, 130, 246, 0.12)" : "rgba(255, 255, 255, 0.03)",
                              color: item.zone ? "var(--primary)" : "var(--text-muted)",
                              border: "1px solid " + (item.zone ? "rgba(59, 130, 246, 0.3)" : "var(--border-color)"),
                              transition: "all 0.15s ease",
                              maxWidth: "150px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                            title="Click to change zone location"
                          >
                            <MapPin size={13} style={{ color: item.zone ? "var(--primary)" : "var(--text-muted)", flexShrink: 0 }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.zone || "Assign Zone..."}</span>
                          </div>
                        </td>
                        <td style={{ textAlign: "center", color: "var(--text-secondary)" }}>
                          {item.orderCount !== undefined ? item.orderCount : "-"}
                        </td>
                      </tr>
                    );
                  })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          );
        });
      })()}

      {/* Create SKU Modal */}
      
      <InventoryCreateModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        categories={categories}
        warehousePos={warehousePos}
        handlePoSelect={handlePoSelect}
        handlePoItemSelect={handlePoItemSelect}
        loadingPoItems={loadingPoItems}
        selectedPoItems={selectedPoItems}
        warehouses={warehouses}
        zones={zones}
        loading={loading}
      />

      <InventoryNotesModal
        isOpen={notesModalOpen}
        onClose={() => {
          setNotesModalOpen(false);
          setSelectedNotesItem(null);
        }}
        item={selectedNotesItem}
        tempNotes={tempNotesText}
        setTempNotes={setTempNotesText}
        onSubmit={handleSaveNotes}
      />

      <InventoryZoneModal
        isOpen={zoneModalOpen}
        onClose={() => {
          setZoneModalOpen(false);
          setSelectedZoneItem(null);
        }}
        item={selectedZoneItem}
        zones={zones}
        onSaveZone={handleSaveZone}
        isSaving={savingZone}
      />

      <InventoryStockModal
        isOpen={stockModalOpen}
        onClose={() => {
          setStockModalOpen(false);
          setSelectedStockItem(null);
        }}
        item={selectedStockItem}
        currentUser={currentUser}
        onSaveStock={handleSaveStock}
        isSaving={savingStock}
      />

      <InventoryShipModal
        isOpen={shipModalOpen}
        onClose={() => {
          setShipModalOpen(false);
          setShipPo(null);
          setShipmentToUpdate(null);
        }}
        shipPo={shipPo}
        loadingShipment={loadingShipment}
        isEditingAddress={isEditingAddress}
        setIsEditingAddress={setIsEditingAddress}
        editAddressData={editAddressData}
        setEditAddressData={setEditAddressData}
        savingAddress={savingAddress}
        handleSaveAddress={handleSaveAddress}
        shipChecklist={shipChecklist}
        toggleShipArrived={toggleShipArrived}
        shipShowPartialInput={shipShowPartialInput}
        toggleShipPartialInput={toggleShipPartialInput}
        handleShipItemQtyChange={handleShipItemQtyChange}
        toggleShipFlagged={toggleShipFlagged}
        shipShowFlagInput={shipShowFlagInput}
        handleShipItemFlagReasonChange={handleShipItemFlagReasonChange}
        handleShipItemNotesChange={handleShipItemNotesChange}
        shipGeneralNotes={shipGeneralNotes}
        setShipGeneralNotes={setShipGeneralNotes}
        actualShippingCost={actualShippingCost}
        setActualShippingCost={setActualShippingCost}
        onSubmit={handleShipSubmit}
        isPartialShipment={isPartialShipment}
        shipmentToUpdate={shipmentToUpdate}
        carriers={carriers}
      />

      <EditShippingCostModal
        isOpen={editCostModalOpen}
        onClose={() => {
          setEditCostModalOpen(false);
          setEditCostContext(null);
        }}
        shipment={editCostContext?.shipment}
        batch={editCostContext?.batch}
        batchIndex={editCostContext?.batchIndex}
        totalBatches={editCostContext?.totalBatches || 1}
        po={findPo(editCostContext?.shipment?.poId, editCostContext?.shipment?.poNumber)}
        currentCost={editCostContext?.batch?.actualShippingCost ?? editCostContext?.shipment?.actualShippingCost ?? 0}
        onSave={handleSaveShipmentCost}
      />

      {selectedChangeRequestPo && (
        <ChangeRequestModal
          po={selectedChangeRequestPo}
          onClose={() => setSelectedChangeRequestPo(null)}
        />
      )}

      {selectedTasksPo && (
        <POTasksModal
          isOpen={!!selectedTasksPo}
          po={selectedTasksPo}
          onClose={() => setSelectedTasksPo(null)}
        />
      )}

      <style>{`
        /* Custom Receiving Checklist Switch */
        .switch {
          position: relative;
          display: inline-block;
          width: 44px;
          height: 24px;
          flex-shrink: 0;
        }

        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: var(--bg-input);
          border: 1px solid var(--border-color);
          transition: .2s;
          border-radius: 24px;
        }

        .slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background-color: var(--text-muted);
          transition: .2s;
          border-radius: 50%;
        }

        input:checked + .slider {
          background-color: rgba(76, 175, 80, 0.1);
          border-color: var(--success);
        }

        input:checked + .slider:before {
          transform: translateX(20px);
          background-color: var(--success);
        }

        .table-actions-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }

        .search-box-container {
          position: relative;
          flex: 1;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: var(--text-muted);
        }

        .search-input {
          padding-left: 2.75rem;
        }

        .sku-badge {
          font-family: var(--font-heading);
          font-weight: 700;
          color: var(--primary);
          background-color: var(--primary-light);
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.85rem;
        }

        /* Inline editable input */
        .inline-edit-input {
          background: transparent;
          border: 1px solid transparent;
          outline: none;
          color: inherit;
          width: 100%;
          font-family: inherit;
          font-size: inherit;
          padding: 0.4rem 0.5rem;
          border-radius: 4px;
          transition: background 0.15s, border-color 0.15s;
        }

        .inline-edit-input:hover:not(:disabled) {
          background-color: hsla(255, 255, 255, 0.02);
          border-color: var(--border-color);
        }

        .inline-edit-input:focus:not(:disabled) {
          background-color: var(--bg-input);
          border-color: var(--primary);
          box-shadow: 0 0 0 1px var(--border-focus);
          color: var(--text-primary);
        }

        .inline-edit-input.text-center {
          text-align: center;
        }

        .inline-edit-input.text-right {
          text-align: right;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 5rem 0;
          gap: 1rem;
          color: var(--text-secondary);
        }

        .spinner-icon {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .po-group-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          padding: 0.75rem 1rem;
          background-color: rgba(255, 255, 255, 0.03);
          border: 1px solid var(--border-color);
          border-bottom: none;
          border-top-left-radius: 6px;
          border-top-right-radius: 6px;
          cursor: pointer;
          user-select: none;
          gap: 0.75rem 1rem;
        }

        .po-group-header[data-collapsed="true"] {
          border-bottom: 1px solid var(--border-color);
          border-bottom-left-radius: 6px;
          border-bottom-right-radius: 6px;
        }

        .po-group-header-left {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          flex: 1 1 auto;
          min-width: 280px;
        }

        .po-group-header-right {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          flex: 0 1 auto;
          max-width: 100%;
        }

        @media (max-width: 1024px) {
          .table-actions-container {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }
          .po-group-header {
            flex-direction: column;
            align-items: stretch;
            gap: 0.75rem;
            padding: 0.85rem 1rem;
          }
          .po-group-header-left {
            width: 100%;
            min-width: 0;
          }
          .po-group-header-right {
            width: 100%;
            justify-content: flex-start;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}
